import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from '../../../icons';
import { hireEmployee } from '../../../api/hr-employee';
import { employmentTypeOptions } from '../../../lib/employment-type-i18n';
import { getSharedLang, subscribeSharedLang } from '../../../lib/base-i18n';
import { listWorkSchedules } from '../../../api/hr-schedule';
import { JobGradeSelect } from '../../../components/JobGradeSelect';
import { useCompanyCodes } from '../../../lib/company-codes';
import { EMPLOYEES_PATH, ERRORS, errorCode } from './employees-shared';
import { Field } from './EmployeeField';
// 손짠 머리 → 정본 PageHeader · 경로(링크) · 범위 칩(2026-10-17 · web 레인 W7b — lib/page-crumbs.ts)
import { PageHeader } from '../../../components/shared';
import { SaveBar } from '../../../components/shared';
import { crumbLinksFor, crumbsFor, scopeFor } from '../../../lib/page-crumbs';
import { EMPLOYEE_NEW_PATH } from './employees-shared';
import './employees-hr.css';

/**
 * 입사 처리 (2026-09-13 · H · `/erp/hr/employees/new`).
 *
 * 시안 axi-employees-hr-detail 의 입사 처리 화면 — 같은 카드 · 2열 폼 · 추가 상자 · 하단 막대.
 * ★ 동작은 종전 명부의 입사 처리와 **같다**: 계정 + 인사 + 연차를 한 번에 만들고, 역할은 STAFF 고정,
 *   초기 비밀번호는 만든 **그 순간 한 번**만 보인다(저장하지 않으므로 다시 볼 수 없다).
 */
interface HireForm {
  name: string; email: string; employeeNumber: string; hireDate: string;
  position: string; employmentType: string; teamId: string;
}
const EMPTY: HireForm = { name: '', email: '', employeeNumber: '', hireDate: '', position: '', employmentType: 'REGULAR', teamId: '' };

export function EmployeeHirePage(): JSX.Element {
  const codes = useCompanyCodes('hr');
  // 고용형태 칩 — 수습 · 인턴 포함(서버 #1222), 이름은 언어를 따르고 회사 코드가 그 위에 덮는다.
  const lang = useSyncExternalStore(subscribeSharedLang, getSharedLang, getSharedLang);
  const employmentTypes = codes.apply('employment_type', employmentTypeOptions(lang));
  const [form, setForm] = useState<HireForm>(EMPTY);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [issued, setIssued] = useState<{ password: string; annualDays: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 팀 목록은 근무제 화면과 같은 API — 목록을 새로 만들지 않는다(종전과 같다).
    listWorkSchedules().then((d) => setTeams(d.teams)).catch(() => { /* 소속 팀 칸만 못 쓴다 */ });
  }, []);

  const onHire = async () => {
    setBusy(true); setError(null);
    try {
      const res = await hireEmployee({
        name: form.name.trim(),
        email: form.email.trim(),
        employeeNumber: form.employeeNumber.trim() || null,
        hireDate: form.hireDate || null,
        position: form.position.trim() || null,
        employmentType: form.employmentType || null,
        teamId: form.teamId || null,
      });
      setIssued({ password: res.initialPassword, annualDays: res.annualDays });
      setForm(EMPTY);
    } catch (err) {
      setError(ERRORS[errorCode(err)] ?? '입사 처리를 하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hre">
      <PageHeader
        breadcrumb={crumbsFor(EMPLOYEE_NEW_PATH, { leaf: '입사 처리' })}
        breadcrumbLinks={crumbLinksFor(EMPLOYEE_NEW_PATH, { leaf: '입사 처리' })}
        scope={scopeFor(EMPLOYEE_NEW_PATH)}
        title="입사 처리"
      />

      {error && <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error}</p>}

      <section className="hre-card hre-detail" aria-label="입사 처리">
        <div className="hre-panes">
          {issued ? (
            <div className="hre-addbox" role="status">
              <h4>만들었습니다 — 초기 비밀번호는 지금 한 번만 보입니다</h4>
              <p className="hre-pw"><code>{issued.password}</code></p>
              <p className="hre-hint">
                저장하지 않으므로 다시 볼 수 없습니다. 본인에게 전달하고 첫 로그인에서 바꾸게 하세요.
                {issued.annualDays !== null && <> 올해 연차는 <b>{issued.annualDays}일</b>로 계산됐습니다.</>}
              </p>
              <div className="hre-addrow">
                <span className="hre-hint">복사해 전달하세요</span>
                <Link className="hre-btn hre-btn--line hre-btn--sm" to={EMPLOYEES_PATH}>목록으로</Link>
                <button type="button" className="hre-btn hre-btn--sm" onClick={() => setIssued(null)}>한 명 더</button>
              </div>
            </div>
          ) : (
            <>
              <section className="hre-fsec"><h3>새 사람 — 계정 + 인사 + 연차를 한 번에</h3><div className="hre-fgrid">
                <Field label="성명" required>
                  {(a) => <input {...a} className="hre-in" type="text" value={form.name} placeholder="홍길동"
                    onChange={(e) => setForm({ ...form, name: e.target.value })} />}
                </Field>
                <Field label="회사 이메일" required>
                  {(a) => <input {...a} className="hre-in" type="email" value={form.email} placeholder="name@company.co.kr"
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />}
                </Field>
                <Field label="사번">
                  {(a) => <input {...a} className="hre-in num" type="text" value={form.employeeNumber} placeholder="비우면 없음"
                    onChange={(e) => setForm({ ...form, employeeNumber: e.target.value })} />}
                </Field>
                <Field label="입사일" hint="연차는 입사일 기준으로 계산됩니다">
                  {(a) => <input {...a} className="hre-in num" type="date" value={form.hireDate}
                    onChange={(e) => setForm({ ...form, hireDate: e.target.value })} />}
                </Field>
                <Field label="직위">
                  {(a) => <JobGradeSelect id={a.id} className="hre-in" value={form.position} manage onChange={(v) => setForm({ ...form, position: v })} />}
                </Field>
                <Field label="소속 팀">
                  {(a) => (
                    <select {...a} className="hre-in" value={form.teamId} onChange={(e) => setForm({ ...form, teamId: e.target.value })}>
                      <option value="">회사 직속</option>
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                </Field>
                <div className="hre-f hre-f--wide"><span className="hre-fl" id="hre-hire-type">고용 형태</span>
                  <span className="hre-chips" role="radiogroup" aria-labelledby="hre-hire-type">
                    {employmentTypes.map((t) => (
                      <label key={t.key} className="hre-choice">
                        <input type="radio" name="hre-hire-type" checked={form.employmentType === t.key}
                          onChange={() => setForm({ ...form, employmentType: t.key })} />
                        <span>{t.label}</span>
                      </label>
                    ))}
                  </span></div>
              </div></section>
              <div className="hre-addrow">
                <span className="hre-hint">
                  권한은 <b>직원(일반 권한)으로 고정</b>입니다 — 인사 담당은 권한을 올릴 수 없고, 관리 권한은 «사용자 관리»에서 줍니다. 이미 있는 계정의 비밀번호를 바꾸는 것도 관리 콘솔의 일입니다.
                </span>
              </div>
            </>
          )}
        </div>
        {!issued && (
          /* 저장 줄 한 벌(2026-10-18 · web 레인 W8) — 종전 `hre-savebar` 의 안내 · 취소 · 입사 처리를 글 · 조건 그대로.
             켜지는 조건 한 줄은 시안 50 «성명 · 회사 이메일을 넣으면 … 켜집니다»(단추 이름은 지금 이름 그대로). */
          <SaveBar
            className="hre-savebar"
            note="입사 처리하면 계정 · 인사 · 연차가 함께 만들어집니다"
            secondary={<Link className="hre-btn hre-btn--line hre-btn--sm" to={EMPLOYEES_PATH}>취소</Link>}
            canSave={!busy && Boolean(form.name.trim()) && Boolean(form.email.trim())}
            onSave={() => { void onHire(); }}
            saving={busy}
            saveLabel={busy ? '만드는 중…' : '입사 처리'}
            enableHint="성명 · 회사 이메일을 넣으면 «입사 처리»가 켜집니다"
          />
        )}
      </section>
    </div>
  );
}

export default EmployeeHirePage;
